import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Firebase
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
export const auth = getAuth(app);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId || undefined);
export const googleProvider = new GoogleAuthProvider();

import { UserProfile, UserRole, AccountTier, normalizeAccountTier } from '../types/user';
export type { UserProfile } from '../types/user';
import { purgeUserSessionData } from './sessionPurge';

export const AUTHORIZED_SUPER_ADMIN_EMAILS = ['bijen.rohilla@gmail.com', 'vedangajyotisha108@gmail.com'];

// Ensure User Document in Firestore
export async function syncUserProfile(
  user: { uid: string; email: string; displayName?: string; photoURL?: string },
  preferredRole?: UserRole | string,
  provider: 'google' | 'email-otp' | 'mobile-otp' | 'password' | 'custom' = 'google'
): Promise<UserProfile> {
  const userRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userRef);

  const cleanEmail = (user.email || '').toLowerCase().trim();
  const isSuperAdminEmail = AUTHORIZED_SUPER_ADMIN_EMAILS.includes(cleanEmail);
  const isTesterEmail = cleanEmail.startsWith('tester@') || cleanEmail === 'tester' || cleanEmail.includes('+tester');
  
  let initialRole: UserRole = UserRole.BASIC_USER;
  if (isSuperAdminEmail) {
    initialRole = UserRole.SUPER_ADMIN;
  } else if (isTesterEmail || preferredRole === UserRole.TESTER || preferredRole === 'tester') {
    initialRole = UserRole.TESTER;
  } else if (preferredRole && Object.values(UserRole).includes(preferredRole as UserRole)) {
    initialRole = preferredRole as UserRole;
  } else if (preferredRole === 'admin' || preferredRole === 'super_user') {
    initialRole = UserRole.ADMIN;
  }

  const initialTier: AccountTier = isSuperAdminEmail || isTesterEmail ? AccountTier.ACHARYA : AccountTier.GUEST;

  if (snap.exists()) {
    const existing = snap.data() as UserProfile;
    // Preserve existing role if set, unless super admin override or tester detection is required
    const targetRole = isSuperAdminEmail
      ? UserRole.SUPER_ADMIN
      : (isTesterEmail ? UserRole.TESTER : (existing.role || initialRole));

    const isTesterUser = isTesterEmail || targetRole === UserRole.TESTER;
    const baseRole = existing.baseRole || targetRole;
    const activeRole = isTesterUser ? (existing.activeRole || baseRole) : baseRole;

    const normalizedTier = normalizeAccountTier(existing.tier || initialTier);

    const updatedProfile: UserProfile = {
      ...existing,
      role: activeRole,
      baseRole,
      activeRole,
      isTester: isTesterUser,
      testMode: isTesterUser ? (activeRole !== baseRole) : false,
      allowedTestRoles: isTesterUser ? [UserRole.BASIC_USER, UserRole.CONSULTING_ASTROLOGER, UserRole.TESTER] : [],
      tier: normalizedTier.canonicalTier,
      historicalTier: existing.historicalTier || normalizedTier.historicalTier,
      tierMigrationStatus: existing.tierMigrationStatus || normalizedTier.migrationStatus,
      hasPracticeEntitlement: existing.hasPracticeEntitlement ?? (isSuperAdminEmail || isTesterUser),
      hasConsultationEntitlement: existing.hasConsultationEntitlement ?? (isSuperAdminEmail || isTesterUser)
    };

    if (targetRole !== existing.role || baseRole !== existing.baseRole) {
      await updateDoc(userRef, {
        role: updatedProfile.role,
        baseRole: updatedProfile.baseRole,
        activeRole: updatedProfile.activeRole,
        isTester: updatedProfile.isTester,
        updatedAt: new Date().toISOString()
      });
    }

    return updatedProfile;
  } else {
    const isTesterUser = isTesterEmail || initialRole === UserRole.TESTER;
    const newProfile: UserProfile = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || user.email.split('@')[0],
      photoURL: user.photoURL || '',
      role: initialRole,
      baseRole: initialRole,
      activeRole: initialRole,
      isTester: isTesterUser,
      testMode: false,
      allowedTestRoles: isTesterUser ? [UserRole.BASIC_USER, UserRole.CONSULTING_ASTROLOGER, UserRole.TESTER] : [],
      tier: initialTier,
      hasPracticeEntitlement: isSuperAdminEmail || isTesterUser,
      hasConsultationEntitlement: isSuperAdminEmail || isTesterUser,
      authProvider: provider,
      createdAt: new Date().toISOString()
    };
    await setDoc(userRef, newProfile);
    return newProfile;
  }
}

// Check Redirect Auth Result after page reload (safely handle iframe & IndexedDB lifecycle)
export async function checkRedirectResult(): Promise<UserProfile | null> {
  try {
    if (typeof window === 'undefined') return null;
    const result = await getRedirectResult(auth).catch((err: any) => {
      const msg = err?.message || String(err);
      // Suppress benign iframe IndexedDB closure / hidden tab events
      if (
        msg.includes('Database is closing') ||
        msg.includes('closing/hidden') ||
        msg.includes('indexedDB') ||
        err?.code === 'auth/no-auth-event'
      ) {
        return null;
      }
      return null;
    });

    if (result && result.user) {
      const fbUser = result.user;
      return await syncUserProfile(
        {
          uid: fbUser.uid,
          email: fbUser.email || '',
          displayName: fbUser.displayName || '',
          photoURL: fbUser.photoURL || ''
        },
        undefined,
        'google'
      );
    }
  } catch (error: any) {
    const msg = error?.message || String(error);
    if (
      !msg.includes('Database is closing') &&
      !msg.includes('closing/hidden') &&
      !msg.includes('indexedDB')
    ) {
      console.warn('Notice checking redirect auth result:', msg);
    }
  }
  return null;
}

// Sign in with Google (Popup preferred for sandbox/iframe compliance)
export async function signInWithGoogle(inviteToken?: string): Promise<UserProfile> {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const fbUser = result.user;

    // Pattern B: Server-Authoritative OAuth Verification & Orphan Protection
    try {
      const idToken = await fbUser.getIdToken();
      const verifyRes = await fetch('/api/auth/oauth-invitation-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, inviteToken }),
      });

      if (!verifyRes.ok) {
        const errData = await verifyRes.json();
        await auth.signOut();
        throw new Error(errData.error || 'Access Denied: Invitation required for new account registration.');
      }
    } catch (verifyErr: any) {
      if (verifyErr?.message?.includes('Access Denied')) {
        throw verifyErr;
      }
      console.warn('OAuth verification network notice:', verifyErr?.message);
    }

    return await syncUserProfile(
      {
        uid: fbUser.uid,
        email: fbUser.email || '',
        displayName: fbUser.displayName || '',
        photoURL: fbUser.photoURL || ''
      },
      undefined,
      'google'
    );
  } catch (error: any) {
    console.warn('Google signInWithPopup notice:', error?.code || error?.message);
    if (
      error?.code === 'auth/popup-blocked' ||
      error?.code === 'auth/cancelled-popup-request' ||
      error?.message?.includes('popup')
    ) {
      try {
        await signInWithRedirect(auth, googleProvider);
      } catch (redirectErr: any) {
        console.warn('Redirect notice:', redirectErr?.message);
      }
      throw new Error('Please enable popups or complete Google authentication in the popup window.');
    }
    throw error;
  }
}

// Sign Out
export async function logoutUser(): Promise<void> {
  try {
    const rawSession = localStorage.getItem('astro_custom_session');
    if (rawSession) {
      try {
        const sessionObj = JSON.parse(rawSession);
        const token = sessionObj?.token || sessionObj?.sessionToken;
        if (token) {
          await fetch('/api/auth/logout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ token })
          });
        }
      } catch {}
    }
    await firebaseSignOut(auth);
  } catch {}
  purgeUserSessionData();
}
